import React from 'react';
import { Formik } from 'formik';

import { useRequestParentContactCode, useVerifyParentContact } from '../../../lib/services/parent.service';
import { IParentVerifyResult } from '../../../lib/types';
import { TwoStepOtpForm } from '../../_core/TwoStepOtpForm/two-step-otp-form.component';
import { useTwoStepOtp } from '../../_core/TwoStepOtpForm/use-two-step-otp';
import {
  otpRequestValidationSchema,
  otpVerifyValidationSchema,
} from '../../_core/TwoStepOtpForm/two-step-otp-form.validation';

const COPY = {
  requestHeading: 'Add another email or phone',
  requestDescription: 'Enter an email or phone number you want to verify. We\'ll send a one-time code. '
    + 'If it is already on another account, verifying it moves that account\'s players here.',
  verifySubmitLabel: 'Verify & add',
};

interface IParentVerifyContactContainerProps {
  // Fires after a successful in-session verify. Refetches of parentSelf and
  // parentPlayers are handled by useVerifyParentContact's refetchQueries.
  // `result.takeover` is non-null when the address was moved off another account.
  onSuccess?: (result: IParentVerifyResult) => void;
  // Seeds the address field when the caller already knows it (the RSVP banner
  // knows which contact the link was sent to). Still editable.
  initialIdentifier?: string;
}

export const ParentVerifyContactContainer: React.FC<IParentVerifyContactContainerProps> = ({
  onSuccess,
  initialIdentifier = '',
}) => {
  const [requestCode] = useRequestParentContactCode();
  const [verifyContact] = useVerifyParentContact();

  const otp = useTwoStepOtp({
    onRequest: async (destination) => {
      await requestCode({ variables: { destination } });
    },
    onVerify: async (destination, code) => {
      const response = await verifyContact({ variables: { destination, code } });
      const result: IParentVerifyResult | undefined = response.data?.parentVerifyContact;
      // In-session contract: response.token must be null (we're already
      // authenticated). A non-null token means the server misclassified.
      if (result?.token) throw new Error('Server returned a session token unexpectedly.');
      if (result) onSuccess?.(result);
    },
  });

  // key={otp.step} forces a fresh Formik mount on each step transition — see
  // the matching comment in parent-login-form.container.tsx.
  if (otp.step === 'verify') {
    return (
      <Formik
        key="verify"
        initialValues={{ code: '' }}
        validationSchema={otpVerifyValidationSchema}
        onSubmit={otp.handleVerify}
      >
        {(form) => (
          <TwoStepOtpForm
            step="verify"
            form={form}
            destination={otp.destination}
            submitError={otp.submitError}
            onBack={otp.onBack}
            copy={COPY}
            resendSecondsRemaining={otp.resendSecondsRemaining}
            resending={otp.resending}
            onResend={otp.handleResend}
          />
        )}
      </Formik>
    );
  }

  return (
    <Formik
      key="request"
      initialValues={{ identifier: initialIdentifier }}
      validationSchema={otpRequestValidationSchema}
      onSubmit={otp.handleRequest}
    >
      {(form) => (
        <TwoStepOtpForm
          step="request"
          form={form}
          destination={otp.destination}
          submitError={otp.submitError}
          onBack={otp.onBack}
          copy={COPY}
        />
      )}
    </Formik>
  );
};
